import React, { useCallback, useState } from "react";
import { Grid, Typography } from "@mui/material";
import DownloadCardComponent from "components/cards/download/DownloadCard.component";
import DatePickerComponent from "components/datePicker/DatePicker.component";
import { DateTime } from "luxon";
import TitleSectionComponent from "components/cards/title/TitleSection.component";
import { useTranslation } from "react-i18next";
import Request from "core/requests";
import { useDispatch, useSelector } from "react-redux";
import { refreshToken, logout } from "store/user.store";
import { openSnackbar } from "store/slices/snackbar";
import YearMonthPickerComponent from "components/datePicker/YearMonthPicker.component";

const HistoricalDataContainer = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { token, user } = useSelector((state) => state.user);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [dateDay, setDateDay] = useState(
    DateTime.now().minus({ days: 1 }) //~> this time yesterday
  );
  const [dateMonth, setDateMonth] = useState(DateTime.now());

  const onClickDateDay = useCallback((value) => {
    setDateDay(value);
  }, []);

  const onClickDateMonth = useCallback((value) => {
    setDateMonth(value);
  }, []);

  const onFailure = useCallback((error) => {
    if (error?.response?.status === 404) {
      dispatch(
        openSnackbar({
          open: true,
          message: t("Archive not available for the selected day"),
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
    } else {
      dispatch(
        openSnackbar({
          open: true,
          message: error.response?.data?.message ? error.response.data.message : "Errore",

          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
    }

    // eslint-disable-next-line
  }, []);

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onError = useCallback(
    (error) => {
      if (isFirstRefresh) {
        if (error?.response?.status === 403) {
          onLogout(error);
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );
  const onSuccess = useCallback((resp) => {
    window.open(resp.url, "_blank");
    setIsFirstRefresh(true);
    // eslint-disable-next-line
  }, []);

  const onDownload = useCallback(
    (date, month) => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const newDate = DateTime.fromISO(date, { zone: timezone });
      const dateUTC = month
        ? newDate.toFormat("yyyy-MM")
        : newDate.toUTC().toISODate();

      Request.stations
        .downloadHistoricalData({
          token: token,
          date: dateUTC,
        })
        .then((resp) => {
          onSuccess(resp);
        })
        .catch((error) => onError(error));
    },
    // eslint-disable-next-line
    [token, dateDay]
  );

  return (
    <Grid
      container
      spacing={2}
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
    >
      <Grid item xs={12}>
        <TitleSectionComponent title={t("Historical data")} direction='column'>
          <Typography variant="caption" sx={{ fontSize: 12 }}>
            {t('Download the archive containing the data collected every 10 minutes, divided by monitoring point')}
          </Typography>
        </TitleSectionComponent>
      </Grid>

      <Grid item container xs={12} sm={5.95} md={5.95} lg={5.95} spacing={1}>
        <Grid item xs={12}>
          <DatePickerComponent
            minDate={DateTime.local(2022, 11, 7)}
            onClickDate={onClickDateDay}
            value={dateDay} />
        </Grid>
        <Grid item xs={12}>
          <DownloadCardComponent
            title={t('Daily archive')}
            onDownload={() => onDownload(dateDay, false)}
            dateSelected={
              i18n.language === "en"
                ? new DateTime(dateDay).toFormat("yyyy-MM-dd")
                : new DateTime(dateDay).toFormat("dd-MM-yyyy")
            }
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} sm={5.95} md={5.95} lg={5.95} spacing={1}>
        <Grid item xs={12}>
          <YearMonthPickerComponent
            minDate={DateTime.local(2022, 11, 7)}
            onClickDate={onClickDateMonth}
            value={dateMonth}
          />
        </Grid>
        <Grid item xs={12}>
          <DownloadCardComponent
            title={t('Monthly Archive')}
            onDownload={() => onDownload(dateMonth, true)}
            dateSelected={new DateTime(dateMonth).toLocaleString(
              { month: "long", year: "numeric" },
              {
                locale: i18n?.language ? i18n.language : "it",
              }
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default HistoricalDataContainer;
